/**
 * FirebaseFirestoreClient — Production implementation backed by the Firebase SDK.
 *
 * All reads and writes go directly through the Firestore SDK.
 * The SDK's persistentLocalCache (IndexedDB) handles offline caching.
 * No custom in-memory cache or write queue — writes are awaited directly.
 *
 * Created once in {@link firebase.ts} at app startup and installed via
 * {@link setFirestoreClient}.
 */

import type { Firestore } from "firebase/firestore";
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  orderBy,
  where,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

import type {
  FirestoreClient,
  FirestoreDocSnapshot,
  FirestoreQueryResult,
  FirestoreConstraint,
  FirestoreTimestamp,
} from "./firestoreClient";

export class FirebaseFirestoreClient implements FirestoreClient {
  private readonly db: Firestore;

  constructor(db: Firestore) {
    this.db = db;
  }

  // ---- helpers ----

  /** Build a Firestore DocumentReference from path segments. */
  private docRef(path: string[]) {
    return doc(this.db, path[0], ...path.slice(1));
  }

  /** Build a Firestore CollectionReference from path segments. */
  private collRef(path: string[]) {
    return collection(this.db, path[0], ...path.slice(1));
  }

  /** Adapt a Firebase DocumentSnapshot to our thin interface. */
  private static toDocSnap(
    snap: Awaited<ReturnType<typeof getDoc>>
  ): FirestoreDocSnapshot {
    return {
      id: snap.id,
      exists: () => snap.exists(),
      data: () => snap.data() as Record<string, unknown> | undefined,
    };
  }

  // ---- Document CRUD ----

  async getDocument(path: string[]): Promise<FirestoreDocSnapshot> {
    const snap = await getDoc(this.docRef(path));
    return FirebaseFirestoreClient.toDocSnap(snap);
  }

  async setDocument(
    path: string[],
    data: Record<string, unknown>,
    options?: { merge?: boolean }
  ): Promise<void> {
    if (options?.merge) {
      await setDoc(this.docRef(path), data, { merge: true });
    } else {
      await setDoc(this.docRef(path), data);
    }
  }

  async updateDocument(
    path: string[],
    data: Record<string, unknown>
  ): Promise<void> {
    await updateDoc(this.docRef(path), data);
  }

  async deleteDocument(path: string[]): Promise<void> {
    await deleteDoc(this.docRef(path));
  }

  // ---- Collection queries ----

  async queryCollection(
    collectionPath: string[],
    constraints: FirestoreConstraint[] = []
  ): Promise<FirestoreQueryResult> {
    const collRef = this.collRef(collectionPath);

    const queryConstraints = constraints.map((c) => {
      if (c.type === "orderBy") {
        return orderBy(c.field, c.direction);
      }
      return where(c.field, c.operator, c.value);
    });

    const q =
      queryConstraints.length > 0
        ? query(collRef, ...queryConstraints)
        : query(collRef);

    const snapshot = await getDocs(q);

    const docs: FirestoreDocSnapshot[] = snapshot.docs.map((d) => ({
      id: d.id,
      exists: () => d.exists(),
      data: () => d.data() as Record<string, unknown>,
    }));

    return { docs, empty: docs.length === 0 };
  }

  // ---- Real-time listeners ----

  onDocumentSnapshot(
    path: string[],
    onNext: (snapshot: FirestoreDocSnapshot) => void,
    onError?: (error: Error) => void
  ): () => void {
    return onSnapshot(
      this.docRef(path),
      (snap) => {
        onNext({
          id: snap.id,
          exists: () => snap.exists(),
          data: () => snap.data() as Record<string, unknown> | undefined,
        });
      },
      onError
    );
  }

  onCollectionSnapshot(
    collectionPath: string[],
    constraints: FirestoreConstraint[],
    onNext: (result: FirestoreQueryResult) => void,
    onError?: (error: Error) => void
  ): () => void {
    const collRef = this.collRef(collectionPath);
    const queryConstraints = constraints.map((c) => {
      if (c.type === "orderBy") {
        return orderBy(c.field, c.direction);
      }
      return where(c.field, c.operator, c.value);
    });
    const q =
      queryConstraints.length > 0
        ? query(collRef, ...queryConstraints)
        : query(collRef);

    return onSnapshot(
      q,
      (snapshot) => {
        onNext({
          docs: snapshot.docs.map((d) => ({
            id: d.id,
            exists: () => d.exists(),
            data: () => d.data() as Record<string, unknown>,
          })),
          empty: snapshot.empty,
        });
      },
      onError
    );
  }

  // ---- ID generation ----

  generateId(collectionPath: string[]): string {
    return doc(this.collRef(collectionPath)).id;
  }

  // ---- Timestamps ----

  serverTimestamp(): unknown {
    return serverTimestamp();
  }

  timestampFromDate(date: Date): FirestoreTimestamp {
    const ts = Timestamp.fromDate(date);
    return {
      toDate: () => ts.toDate(),
      seconds: ts.seconds,
      nanoseconds: ts.nanoseconds,
    };
  }
}
